import LoadingErrorWrapper from '../shared_components/loadingErrorWrapper';
import React, { useState, useRef, useEffect, useMemo, Suspense, useCallback } from 'react';
import { 
  Skeleton,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import { 
  GroupsOutlined,
  CompareArrowsOutlined,
  PeopleOutlined,
  DonutSmallOutlined,
  AutoGraphOutlined,
  PersonPinCircleOutlined,
  Explore
} from '@mui/icons-material';

// Shared Components
import PageLayout from '../shared_components/pageLayout';
import AccordionSection from '../shared_components/accordionSection';
import AccordionContainer from '../shared_components/accordionContainer';
import StickyFilters from '../shared_components/stickyFilters';
import InitialSchoolSelector from '../shared_components/initialSchoolSelector';
import NowWhatSection from '../shared_components/templates/nowWhatTemplate';
import AccordionContentWrapper from '../shared_components/accordionContentWrapper';
import AISummaryAccordion from '../shared_components/AISummaryAccordion';

// Custom Hook
import { useAccordionState } from '../../hooks/useAccordionState';
import { useFeatureUsage, PRODUCT_AREAS } from '../../hooks/useFeatureUsage';

// Utils
import { filterSchools, getAvailableSchoolTypes, getSchoolGrades } from '../../utils/schoolUtils';
import { formatSchoolName } from '../../utils/nameFormatter';
import chartAnimationState from '../../utils/chartAnimationControl';
import { detectSchoolDataType } from '../../utils/schoolDataDetection';

// Stores
import useSchoolStore from '../../stores/schoolStore';

// Cache
import { useTeamSchoolCache } from '../../hooks/teamCacheService';

// Contexts
import { useInsight } from '../../contexts/InsightContext';

// Components
import GeoXNearbyMapPopup from './nearby/GeoXNearbyMapPopup';

// Lazy load components
const GeoXCommunityHeadlines = React.lazy(() => import('./community/GeoXCommunityHeadlines'));
const GeoXEnrollmentHeadlines = React.lazy(() => import('./enrollment/GeoXEnrollmentHeadlines'));
const GeoXNearbyHeadlines = React.lazy(() => import('./nearby/GeoXNearbyHeadlines'));


function GeographicExplorer() {

  const trackUsage = useFeatureUsage(PRODUCT_AREAS.GEOGRAPHIC_EXPLORER);

  // Get data from team school cache
    const { 
      schools, 
      loading: schoolsLoading, 
    } = useTeamSchoolCache();

  // Use the school store hook
  const {
    selectedSchool,
    esriData,
    nearbySchools,
    schoolsEnrollmentData,
    districtBoundaries,
    error,
    availableTravelModes,
    selectedTravelMode,
    changeTravelMode,
    loadingStates = {},
  } = useSchoolStore();

  // Context declarations
  const { setToolAndSection } = useInsight();
  const initialSchoolLoadRef = useRef(false);
  const initialDropAPinGradesSet = useRef(false);
  const gradesInitializedForSchoolRef = useRef(null);

  // State declarations
  const [governanceFilter, setGovernanceFilter] = useState(['Charter', 'District']);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedDriveTime, setSelectedDriveTime] = useState(10);
  const [schoolTypeFilter, setSchoolTypeFilter] = useState([]);
  const [availableSchoolTypes, setAvailableSchoolTypes] = useState([]);

  // Derive which drive times actually have data for the selected school
  const availableDriveTimes = useMemo(() => {
    if (!esriData?.drive_times) return null;
    const times = Object.keys(esriData.drive_times).map(Number).sort((a, b) => a - b);
    return times.length > 0 ? times : null;
  }, [esriData]);

  // Auto-reset drive time when the available options change and current selection isn't in the list
  useEffect(() => {
    if (availableDriveTimes && !availableDriveTimes.includes(selectedDriveTime)) {
      const fallback = availableDriveTimes.includes(10) ? 10 : availableDriveTimes[0];
      setSelectedDriveTime(fallback);
    }
  }, [availableDriveTimes]); // eslint-disable-line react-hooks/exhaustive-deps
  const {
    expandedSections,
    handleExpandedChange: baseHandleExpandedChange,
    handleExpandAll,
    handleCollapseAll,
  } = useAccordionState({
    communityHeadlines: false,
    enrollmentHeadlines: false,
    comparisonHeadlines: false,
    nowWhat: false,
  });


  const isSectionDataLoaded = useCallback((sectionId) => {
    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    
    switch (sectionId) {
      case 'communityHeadlines':
        return !!esriData && !!selectedSchool && !loadingStates.marketShare;
        
      case 'enrollmentHeadlines': {
        // For locations without enrollment data, this section is always "ready" but will show alternative content.
        // Readiness depends on the nearby-schools response having arrived, not on it containing
        // anything: an empty travel-time zone is a real answer, and the section explains it rather
        // than sitting disabled behind a loading spinner forever.
        if (isDropAPinLocation) {
          return !!nearbySchools &&
                 selectedGrades.length > 0 &&
                 !loadingStates.nearby &&
                 !loadingStates.enrollment;
        }
        
        // For schools with enrollment data, check if we have the data.
        // Grades are required too: GeoXEnrollmentHeadlines renders nothing without them, and they
        // initialize asynchronously, so without this the section unlocks into a blank body.
        const hasSelectedSchoolData = schoolsEnrollmentData.some(record => record.uuid === selectedSchool?.uuid);
        
        return !!selectedSchool &&
               hasSelectedSchoolData &&
               selectedGrades.length > 0 &&
               !loadingStates.enrollment;
      }
      
      case 'comparisonHeadlines': {
        // For all locations (with or without enrollment), we need nearby schools data
        const nearbySchoolIds = nearbySchools?.current?.[selectedDriveTime]?.map(s => s.uuid) || [];
        
        if (isDropAPinLocation) {
          // As above, an empty zone is a valid result: the comparison panels still render the
          // target location on its own, so don't gate the section on having neighbours.
          return !!nearbySchools && 
                 !!selectedSchool &&
                 selectedGrades.length > 0 &&
                 !loadingStates.nearby &&
                 !loadingStates.enrollment;
        }
        
        // For schools with enrollment data, we need both nearby schools and enrollment data
        const hasAllEnrollmentData = nearbySchoolIds.every(
          id => schoolsEnrollmentData.some(record => record.uuid === id)
        );
        const hasSelectedSchoolData = schoolsEnrollmentData.some(record => record.uuid === selectedSchool?.uuid);
        
        return !!nearbySchools && 
               !!selectedSchool &&
               hasSelectedSchoolData &&
               (nearbySchoolIds.length === 0 || hasAllEnrollmentData) &&
               !loadingStates.nearby && 
               !loadingStates.enrollment;
      }
               
      default: 
        return false;
    }
  }, [esriData, nearbySchools, schoolsEnrollmentData, selectedSchool, selectedDriveTime, selectedGrades, loadingStates]);

  // Accordion readiness is derived from store data on every render rather than tracked in local
  // state. A separate flag can only ever lag or lead the data it describes, which is what made
  // sections openable while their contents were still empty.
  const sectionReady = useMemo(() => ({
    communityHeadlines: isSectionDataLoaded('communityHeadlines'),
    enrollmentHeadlines: isSectionDataLoaded('enrollmentHeadlines'),
    comparisonHeadlines: isSectionDataLoaded('comparisonHeadlines'),
    nowWhat: true,
  }), [isSectionDataLoaded]);


  // Handle URL parameters after schools are loaded (initial load)
  useEffect(() => {
    if (schools.length > 0 && !selectedSchool && !initialSchoolLoadRef.current) {
      const params = new URLSearchParams(window.location.search);
      const schoolId = params.get('school');
      
      if (schoolId) {
        // Only support UUID for new implementation
        const school = schools.find(s => s.uuid === schoolId);
        if (school) {
          useSchoolStore.getState().selectSchool({
            ...school,
            name: formatSchoolName(school.name)
          });
        }
        initialSchoolLoadRef.current = true;
      }
    }
  }, [schools, selectedSchool]);

  // Watch for URL changes even after a school is already selected
  // This ensures navigation from other tools (like AI Summary) updates the selected school
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const schoolIdFromUrl = params.get('school');
    
    // If URL has a school parameter and it's different from the currently selected school
    if (schoolIdFromUrl && schools.length > 0 && selectedSchool?.uuid !== schoolIdFromUrl) {
      const school = schools.find(s => s.uuid === schoolIdFromUrl);
      if (school) {
        useSchoolStore.getState().selectSchool({
          ...school,
          name: formatSchoolName(school.name)
        });
      }
    }
  }, [window.location.search, schools, selectedSchool?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedSchool) {
      // Close any expanded sections when school changes.
      // Deliberately no markDataStale() here: selectSchool already raises the same loading
      // flags before it fetches, and this effect runs after it. On a cache hit selectSchool
      // finishes synchronously, so re-raising the flags here would strand every section
      // behind a spinner that nothing ever clears.
      Object.keys(expandedSections).forEach(section => {
        if (expandedSections[section]) {
          baseHandleExpandedChange(section)(null, false);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool?.uuid, baseHandleExpandedChange]);

  useEffect(() => {
    if (selectedSchool?.uuid) {
      trackUsage('school_analyzed', { schoolUuid: selectedSchool.uuid });
    }
  }, [selectedSchool?.uuid, trackUsage]);

  // Initialize schools on mount
  useEffect(() => {
    if (schools.length === 0) {
      useSchoolStore.getState().fetchSchools();
    }
  }, [schools.length]);

  // Actions for the Now What section
  const actions = [
    {
      icon: DonutSmallOutlined,
      title: 'Competitive Explorer',
      description: 'Analyze market share trends and projections for schools in your commuity',
      path: `/competitive-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
    },
    {
      icon: AutoGraphOutlined,
      title: 'Projections Explorer',
      description: 'Explore historical and projected enrollment data for the next five years',
      path: `/projections-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
    },
    {
      icon: PersonPinCircleOutlined,
      title: 'Student Catchment Explorer',
      description: 'Connect your student locations to community and competitive insights',
      path: `/student-catchment-explorer${selectedSchool ? `?school=${selectedSchool.uuid}` : ''}`
    }
  ];
  
  // Update tool and section in context
  useEffect(() => {
    // On mount, set the tool with null section to show default content
    setToolAndSection('geographic-explorer', null); 
    
    // On unmount, only clear if we're actually unmounting (not just switching tools)
    return () => {
      // Get all tool components that might be mounted
      const geoExplorerExists = document.querySelector('[data-tool="geographic-explorer"]');
      const compExplorerExists = document.querySelector('[data-tool="competitive-explorer"]');
      
      // Only clear if no tool components are present
      if (!geoExplorerExists && !compExplorerExists) {
        setToolAndSection(null, null);
      }
    };
  }, [setToolAndSection]);
  
  // Update available school types and initialize filter to select all by default
  useEffect(() => {
    if (schoolsEnrollmentData.length > 0) {
      const types = getAvailableSchoolTypes(schoolsEnrollmentData);
      const typesChanged = JSON.stringify(types.sort()) !== JSON.stringify(availableSchoolTypes.sort());
      
      if (typesChanged) {
        setAvailableSchoolTypes(types);
        // When available types change, update filter to include all new types
        if (types.length > 0) {
          setSchoolTypeFilter(types);
        }
      }
    }
  }, [schoolsEnrollmentData, availableSchoolTypes]);

  useEffect(() => {
    // If all sections are collapsed, only clear the section, not the tool
    const hasExpandedSection = Object.values(expandedSections).some(isExpanded => isExpanded);
    if (!hasExpandedSection) {
      setToolAndSection('geographic-explorer', null);  // Keep the tool, just clear the section
    }
  }, [expandedSections, setToolAndSection]);

  // Auto-set grades on school change (one-time init per school selection)
  useEffect(() => {
    if (!selectedSchool) {
      initialDropAPinGradesSet.current = false;
      gradesInitializedForSchoolRef.current = null;
      return;
    }

    // When the school changes, reset the "already initialized" guard so we re-evaluate grades
    // for the new school. Do NOT clear selectedGrades here — the set calls below will overwrite
    // stale grades from the previous school when a valid grade range is found.
    if (gradesInitializedForSchoolRef.current !== selectedSchool.uuid) {
      initialDropAPinGradesSet.current = false;
      gradesInitializedForSchoolRef.current = selectedSchool.uuid;
    }

    if (initialDropAPinGradesSet.current) return;

    const { isDropAPinLocation, isStandardSchool } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);

    // Prefer grade_range from the school object (available immediately from team schools API)
    if (selectedSchool.grade_range) {
      const schoolGrades = getSchoolGrades(selectedSchool.grade_range);
      if (schoolGrades.length > 0) {
        setSelectedGrades(schoolGrades);
        initialDropAPinGradesSet.current = true;
        return;
      }
    }

    // Fallback for drop-a-pins with no grade_range: default to K-12
    if (isDropAPinLocation) {
      setSelectedGrades(['Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']);
      initialDropAPinGradesSet.current = true;
      return;
    }

    // Fallback for NCES schools with no grade_range: derive from enrollment data once it loads
    if (isStandardSchool) {
      const enrollmentRecord = schoolsEnrollmentData?.find(d => d.uuid === selectedSchool.uuid);
      if (enrollmentRecord?.enrollment_by_grade?.current) {
        const derivedGrades = Object.keys(enrollmentRecord.enrollment_by_grade.current)
          .map(key => key === 'Kindergarten' ? 'Kindergarten' : key.replace('Grade ', ''))
          .filter(Boolean);
        if (derivedGrades.length > 0) {
          setSelectedGrades(derivedGrades);
          initialDropAPinGradesSet.current = true;
        }
      }
    }
  // selectedGrades is intentionally NOT in the dependency array to avoid overriding user changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool, schoolsEnrollmentData]);

  // Filter schools based on criteria
  const filteredSchools = useMemo(() => {
    if (!selectedSchool || !nearbySchools) {
      return [];
    }
    
    const { hasEnrollmentData } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    
    // Get all schools for the selected drive time
    const schoolsForSelectedDriveTime = nearbySchools?.current?.[selectedDriveTime] || [];
    
    // For locations without enrollment data, exclude the selected location from filtered schools
    // but still include nearby schools for analysis
    const allSchools = hasEnrollmentData 
      ? [
          { 
            ...selectedSchool, 
            drive_time: 0 
          }, 
          ...schoolsForSelectedDriveTime.map(school => ({
            ...school,
            drive_time: selectedDriveTime
          }))
        ]
      : schoolsForSelectedDriveTime.map(school => ({
          ...school,
          drive_time: selectedDriveTime
        }));
  
    return filterSchools(
      allSchools,
      {
        selectedGrades,
        governanceFilter,
        schoolTypeFilter
      },
      schoolsEnrollmentData
    );
  }, [
    selectedSchool,
    nearbySchools,
    selectedDriveTime,
    selectedGrades,
    governanceFilter,
    schoolTypeFilter,
    schoolsEnrollmentData
  ]);

  const handleExpandedChange = (sectionId) => (event, isExpanded) => {
    // Prevent expanding if data isn't ready
    if (isExpanded && !sectionReady[sectionId]) {
      event.preventDefault(); // Add this line to prevent default behavior
      event.stopPropagation(); // Optional: stop event propagation
      return;
    }
    
    baseHandleExpandedChange(sectionId)(event, isExpanded);
    if (isExpanded) {
      setToolAndSection('geographic-explorer', sectionId);
    } else if (!Object.values(expandedSections).some(expanded => expanded)) {
      setToolAndSection('geographic-explorer', null);
    }
  };
  
  
  const handleSchoolChange = (event) => {
    const schoolId = event.target.value;
    // Only use UUID for school identification
    const school = schools.find(s => s.uuid === schoolId);
    if (school) {
      // Keep ?school= pointing at the new selection. The effect below reselects whatever the
      // URL names whenever the selection differs from it, so leaving a stale param here means
      // a manual pick from the dropdown gets reverted to the school the page was opened with.
      // replaceState rather than pushState: this is the same view, not a new history entry.
      const params = new URLSearchParams(window.location.search);
      if (params.get('school')) {
        params.set('school', school.uuid);
        window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
      }

      useSchoolStore.getState().selectSchool({
        ...school,
        name: formatSchoolName(school.name)
      });
    }
  };

  const handleGradeChange = (event) => {
    const value = event.target.value;
    const newGrades = typeof value === 'string' ? value.split(',') : value;
    
    // Grade changes working correctly
    
    setSelectedGrades(newGrades);
  };

  const handleGovernanceChange = (event) => {
    const value = event.target.value;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    // Require at least one governance type to be selected
    if (newValue.length === 0) {
      return;
    }
    setGovernanceFilter(newValue);
  };

  const handleDriveTimeChange = (event) => {
    setSelectedDriveTime(Number(event.target.value));
  };

  const handleTravelModeChange = (event) => {
    changeTravelMode(event.target.value);
  };

  const handleSchoolTypeChange = (event) => {
    const value = event.target.value;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    // Require at least one school type to be selected
    if (newValue.length === 0) {
      return;
    }
    setSchoolTypeFilter(newValue);
  };

  const SectionLoadingPlaceholder = ({ message }) => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 8 
      }}
    >
      <CircularProgress size={40} sx={{ mb: 2 }} />
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  return (
    <PageLayout
      data-tool="geographic-explorer"
      title="Geographic Explorer"
      description={selectedSchool
        ? "Refine your analysis using the Governance, Grades, and Drive Time filters."
        : "Use this tool to explore the geographic context of schools in your area. Start by selecting a school."
      }
    >
      <LoadingErrorWrapper
        loading={schoolsLoading}
        error={error}
        initialLoading={!schools || !schools.length}
        hideSteps={true}
      >
        {!selectedSchool ? (
          <InitialSchoolSelector
            schools={schools}
            selectedSchool={selectedSchool}
            onSchoolChange={handleSchoolChange}
          />
        ) : (
          <React.Fragment>
            <StickyFilters
              schools={schools}
              selectedSchool={selectedSchool}
              governanceFilter={governanceFilter}
              selectedGrades={selectedGrades}
              selectedDriveTime={selectedDriveTime}
              selectedTravelMode={selectedTravelMode}
              schoolTypeFilter={schoolTypeFilter}
              availableSchoolTypes={availableSchoolTypes}
              availableDriveTimes={availableDriveTimes}
              availableTravelModes={availableTravelModes}
              onSchoolChange={handleSchoolChange}
              onGovernanceChange={handleGovernanceChange}
              onGradeChange={handleGradeChange}
              onDriveTimeChange={handleDriveTimeChange}
              onTravelModeChange={handleTravelModeChange}
              onSchoolTypeChange={handleSchoolTypeChange}
              expandedSections={expandedSections}
              onExpandAll={handleExpandAll}
              onCollapseAll={handleCollapseAll}
              showSchoolType={true}
            />

            {/* AI Summary Accordion - Executive Summary */}
            <AISummaryAccordion
              selectedSchool={selectedSchool}
              driveTime={selectedDriveTime}
              summaryType="geographic"
              travelMode={selectedTravelMode}
              defaultExpanded={true}
            />

            <AccordionContainer 
              expandedSections={expandedSections}
              onSectionChange={handleExpandedChange}
            >
              <AccordionSection
                id="communityHeadlines"
                title="Your Community"
                summary={`Demographic trends of the community within ${selectedDriveTime} minutes of ${selectedSchool?.name || 'the selected school'}`}
                icon={PeopleOutlined}
                disabled={!sectionReady.communityHeadlines} 
                loading={!sectionReady.communityHeadlines}
              >
                <AccordionContentWrapper 
                  sectionId="communityHeadlines"
                  isExpanded={expandedSections.communityHeadlines}
                >
                  <Suspense fallback={<Skeleton variant="rectangular" height={600} />}>
                    {sectionReady.communityHeadlines ? (
                      <GeoXCommunityHeadlines
                        selectedSchool={selectedSchool}
                        esriData={esriData}
                        selectedDriveTime={selectedDriveTime}
                        selectedTravelMode={selectedTravelMode}
                        schoolsEnrollmentData={schoolsEnrollmentData}
                        filteredSchools={filteredSchools}
                        selectedGrades={selectedGrades}
                        governanceFilter={governanceFilter} 
                        animationEnabled={chartAnimationState.shouldAnimate('communityHeadlines', selectedSchool?.uuid)}
                      />
                    ) : (
                      <SectionLoadingPlaceholder 
                        message="Loading community data..." 
                      />
                    )}
                  </Suspense>
                </AccordionContentWrapper>
              </AccordionSection>
  
              <AccordionSection
                id="enrollmentHeadlines"
                title="Enrollment Trends"
                summary={`Historical enrollment and demographic trends for ${selectedSchool?.name || 'the selected school'}`}
                icon={GroupsOutlined}
                disabled={!sectionReady.enrollmentHeadlines}
                loading={!sectionReady.enrollmentHeadlines}
              >
                <AccordionContentWrapper 
                  sectionId="enrollmentHeadlines"
                  isExpanded={expandedSections.enrollmentHeadlines}
                >
                  <Suspense fallback={<Skeleton variant="rectangular" height={200} />}>
                  {sectionReady.enrollmentHeadlines ? (
                    <GeoXEnrollmentHeadlines
                      selectedSchool={selectedSchool}
                      filteredSchools={filteredSchools}
                      schoolsEnrollmentData={schoolsEnrollmentData}
                      selectedGrades={selectedGrades}
                      governanceFilter={governanceFilter} 
                      schoolTypeFilter={schoolTypeFilter}
                      nearbySchools={nearbySchools} 
                      visibleDriveTimes={[selectedDriveTime]}
                      selectedDriveTime={selectedDriveTime}
                      selectedTravelMode={selectedTravelMode}
                      animationEnabled={chartAnimationState.shouldAnimate('enrollmentHeadlines', selectedSchool?.uuid)}
                      />
                    ) : (
                      <SectionLoadingPlaceholder 
                        message="Loading enrollment data..." 
                      />
                    )}
                  </Suspense>
                </AccordionContentWrapper>
              </AccordionSection>
  
              <AccordionSection
                id="comparisonHeadlines"
                title="Nearby Schools Comparison"
                summary={`Interactive analysis of ${selectedSchool?.name || 'the selected school'} and nearby schools`}
                icon={CompareArrowsOutlined}
                disabled={!sectionReady.comparisonHeadlines}
                loading={!sectionReady.comparisonHeadlines}
              >
                <AccordionContentWrapper 
                  sectionId="comparisonHeadlines"
                  isExpanded={expandedSections.comparisonHeadlines}
                >
                  <Suspense fallback={<Skeleton variant="rectangular" height={600} />}>
                  {sectionReady.comparisonHeadlines ? (
                    <GeoXNearbyHeadlines
                      selectedSchool={selectedSchool}
                      filteredSchools={filteredSchools}
                      esriData={esriData}
                      visibleDriveTimes={[selectedDriveTime]}
                      schoolsEnrollmentData={schoolsEnrollmentData}
                      governanceFilter={governanceFilter}
                      selectedGrades={selectedGrades}
                      schoolTypeFilter={schoolTypeFilter}
                      PopupComponent={GeoXNearbyMapPopup}
                      nearbySchools={nearbySchools}
                      districtBoundaries={districtBoundaries}
                      selectedDriveTime={selectedDriveTime}
                      selectedTravelMode={selectedTravelMode}
                      />
                    ) : (
                      <SectionLoadingPlaceholder 
                        message="Loading school comparison data..." 
                      />
                    )}
                  </Suspense>
                </AccordionContentWrapper>
              </AccordionSection>
  
              <AccordionSection
                id="nowWhat"
                title="Now What?"
                summary="Continue your analysis with these related tools"
                icon={Explore}
                disabled={false}
                loading={false}
              >
                <AccordionContentWrapper 
                  sectionId="nowWhat"
                  isExpanded={expandedSections.nowWhat}
                >
                  <Suspense fallback={<Skeleton variant="rectangular" height={600} />}>
                    <NowWhatSection 
                      sectionIcon={Explore}
                      actions={actions}
                    />
                  </Suspense>
                </AccordionContentWrapper>
              </AccordionSection>
            </AccordionContainer>
          </React.Fragment>
        )}
      </LoadingErrorWrapper>
    </PageLayout>
  );
}

export default GeographicExplorer;